import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const money = (n) => (n === 0 ? "$0" : `$${Number(n).toFixed(Number.isInteger(n) ? 0 : 2)}`);

const PLAN_LABELS = {
  free: "FREE", plus: "THE CHEAPER WAY PLUS", annual: "THE CHEAPER WAY ANNUAL", lifetime: "FOUNDING LIFETIME",
};

export default function Pro() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/pro/pricing").then((r) => setData(r.data)).catch(() => {});
    track("pricing_page_view");
  }, []);

  const currentPlan = user ? (user.plan || (user.is_pro ? "plus" : "free")) : null;

  const LOOKUP = { plus: "tcw_plus_monthly", annual: "tcw_annual_yearly", lifetime: "tcw_lifetime_once" };

  const subscribe = async (planId) => {
    if (planId === "free") { nav("/find"); return; }
    if (!user) { track("upgrade_clicked", { plan: planId }); nav("/login"); return; }
    setBusy(planId);
    track(`${planId}_checkout_started`);
    try {
      const { data: res } = await api.post("/payments/checkout", {
        lookup_key: LOOKUP[planId], origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-24 font-display text-3xl text-stone-500">Loading…</div>;

  const founding = data.founding || {};
  const tiers = data.tiers || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="eyebrow mb-4">MEMBERSHIP</div>
      <h1 className="font-display text-4xl md:text-7xl leading-[0.92] mb-4">FIND THE<br />CHEAPER WAY.</h1>
      <p className="font-serif text-xl text-stone-300 max-w-2xl mb-4">Free members get 3 complete Finds every day. Membership unlocks unlimited Finds—nothing complicated.</p>

      {currentPlan && currentPlan !== "free" && (
        <div data-testid="account-status" className="inline-flex flex-wrap items-center gap-3 border border-[#5FB878]/50 bg-[#141217] px-5 py-3 mb-10">
          <span className="font-label text-[10px] tracking-widest text-stone-500">YOUR PLAN</span>
          <span className="font-label text-xs tracking-widest text-[#5FB878]">{PLAN_LABELS[currentPlan]}</span>
          <span className="font-label text-[10px] tracking-widest text-stone-500">
            {currentPlan === "lifetime" ? "· ONE-TIME PURCHASE · NO RENEWAL" : currentPlan === "annual" ? "· BILLED YEARLY" : "· BILLED MONTHLY"}
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
        {tiers.map((t) => {
          const isCurrent = currentPlan === t.id;
          const emphasized = t.best_value;
          const foundingSoldOut = t.founding && !founding.available;
          return (
            <div key={t.id} data-testid={`tier-${t.id}`}
              className={`relative border bg-[#141217] p-7 flex flex-col ${emphasized ? "border-2 border-[#EF3340]" : t.founding ? "border-2 border-[#D4AF37]" : "hairline"}`}>
              {emphasized && <div className="absolute -top-3 left-6 bg-[#EF3340] text-white font-label text-[9px] tracking-widest px-3 py-1">BEST VALUE</div>}
              {t.founding && <div className="absolute -top-3 left-6 bg-[#D4AF37] text-[#0D0B10] font-label text-[9px] tracking-widest px-3 py-1">FOUNDING 500</div>}

              <div className="font-label text-[11px] tracking-widest text-stone-300 mb-2 mt-1">{t.name}</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className={`font-display text-5xl ${emphasized ? "text-[#EF3340]" : t.founding ? "text-[#D4AF37]" : "text-[#F4EEE5]"}`}>{money(t.price)}</span>
                <span className="font-label text-xs text-stone-500">
                  {t.period === "forever" ? "" : t.period === "one-time" ? "one time" : `/${t.period === "year" ? "yr" : "mo"}`}
                </span>
              </div>
              {t.monthly_equiv && <div className="font-label text-[10px] tracking-widest text-stone-500 mb-1">≈ ${t.monthly_equiv}/mo · SAVE ${t.savings_vs_monthly}/yr</div>}
              {t.founding && <div className="font-label text-[10px] tracking-widest text-stone-500 mb-1">{founding.available ? `${founding.remaining} / ${founding.limit} REMAINING` : "SOLD OUT"}</div>}
              <p className="font-serif text-sm text-stone-400 mb-5 mt-1">{t.tagline}</p>

              <ul className="space-y-2.5 mb-7">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 font-serif text-sm text-stone-300">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.id === "free" ? "text-stone-500" : "text-[#5FB878]"}`} /> {f}
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div data-testid={`tier-current-${t.id}`} className="mt-auto font-label text-xs tracking-widest text-[#5FB878] border border-[#5FB878] px-5 py-3.5 text-center">CURRENT PLAN ✓</div>
              ) : foundingSoldOut ? (
                <div className="mt-auto font-label text-xs tracking-widest text-stone-500 border hairline px-5 py-3.5 text-center">SOLD OUT</div>
              ) : (
                <button onClick={() => subscribe(t.id)} disabled={busy === t.id} data-testid={`tier-cta-${t.id}`}
                  className={`mt-auto w-full font-label text-xs tracking-widest px-5 py-3.5 transition-colors disabled:opacity-50 ${
                    t.id === "free" ? "border hairline text-stone-200 hover:border-[#EF3340]"
                    : t.founding ? "bg-[#D4AF37] text-[#0D0B10] hover:bg-[#c39f2e]"
                    : "bg-[#EF3340] text-white hover:bg-[#cc2a35]"}`}>
                  {busy === t.id ? "…" : t.id === "free" ? "START FREE →" : !user ? "LOG IN TO UPGRADE →" : t.id === "lifetime" ? "LOCK IN LIFETIME →" : "UPGRADE →"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="font-label text-[10px] tracking-widest text-stone-600 mt-8 max-w-3xl">
        Lifetime is an early-adopter opportunity, not a promise it stays available. Membership never influences our recommendations — we'll always point you to the free, secondhand, borrowed or non-affiliate option when it's the better call.
      </p>
    </div>
  );
}
